import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://resume-x.app";

// Temporary auth-flow callback tokens or payment callbacks that should not appear in search results.
const STRICT_NOINDEX_PREFIXES = [
  "/verify-email",
  "/reset-password",
  "/2fa-verify",
  "/billing/return",
  "/billing/cancel",
];

function isNoindex(pathname: string): boolean {
  return STRICT_NOINDEX_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/**
 * Centralized, per-route SEO tags.
 *
 * Sets self-referencing canonical and full robots directives
 * (max-snippet, max-image-preview, max-video-preview) for optimal SEO, AEO, and GEO.
 *
 * Blog post detail pages (/blog/<slug>) set their own canonical inside
 * SinglePost.tsx, so we skip canonical there to avoid emitting duplicate tags.
 */
export default function RouteSeo() {
  const { pathname } = useLocation();

  const noindex = isNoindex(pathname);
  const isBlogPost = pathname.startsWith("/blog/") && pathname !== "/blog/";

  const canonicalPath =
    pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  const canonical = `${SITE_URL}${canonicalPath}`;

  useEffect(() => {
    // Remove any conflicting pre-existing canonical tags so exactly one valid canonical URL exists
    const canonicals = document.querySelectorAll('link[rel="canonical"]');
    if (canonicals.length > 1) {
      canonicals.forEach((el, index) => {
        if (index > 0) el.remove();
      });
    }
  }, [canonical]);

  return (
    <Helmet>
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, follow"
            : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        }
      />
      {!isBlogPost && <link rel="canonical" href={canonical} data-react-helmet="true" />}
    </Helmet>
  );
}
